import { Empty } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import CarDetailSection from "../../../cars/components/cardetails/CarDetailSection";
import CarDetailSubTabs from "../../../cars/components/cardetails/CarDetailSubTabs";
import useUrlParamState from "../../../../hooks/useUrlParamState";
import displayValue from "../../../../utilities/displayValue";

function formatDateTime(value, na) {
    if (!value) return na;
    const date = moment(String(value));
    if (!date.isValid()) return na;
    return `${date.format("MM/DD/YYYY")} ${date.format("hh:mm A")}`;
}

function getReservationId(reservation) {
    return reservation?.resId ?? reservation?.id ?? null;
}

function ResCustomer({ reservations, pastReservations, loading = false }) {
    const { t } = useTranslation("cars");
    const na = t("notAvailable");

    const [futureReservations, setFutureReservations] = useState([]);
    const [pastRes, setPastRes] = useState([]);
    const [activeTabParam, setActive] = useUrlParamState("resTab", 1, [1, 2]);
    const active = Number(activeTabParam);

    useEffect(() => {
        setFutureReservations(reservations || []);
        setPastRes(pastReservations || []);
    }, [reservations, pastReservations]);

    const renderRow = (data) => {
        const reservationId = getReservationId(data);

        return (
            <tr
                className="border-b border-slate-100 last:border-0 transition-colors hover:bg-slate-50/60"
                key={data.id || reservationId}>
                <td className="px-4 py-3.5 font-mono text-xs text-slate-700">
                    {displayValue(reservationId, na)}
                </td>
                <td className="px-4 py-3.5 text-slate-800">{displayValue(data.customerName, na)}</td>
                <td className="px-4 py-3.5 text-slate-600">{formatDateTime(data.pickupDate, na)}</td>
                <td className="px-4 py-3.5 text-slate-600">{formatDateTime(data.dropoffDate, na)}</td>
                <td className="px-4 py-3.5 text-right">
                    {data.id ? (
                        <Link className="no-underline" to={`/reservations/${data.id}`}>
                            <span className="viewbb fontbold">{t("view")}</span>
                        </Link>
                    ) : (
                        na
                    )}
                </td>
            </tr>
        );
    };

    const currentList = active === 1 ? futureReservations : pastRes;
    const emptyMessage =
        active === 1 ? t("carDetail.noReservationHistory") : t("carDetail.noCompletedReservations");

    const subTabs = [
        { id: 1, label: t("carDetail.upcomingReservations") },
        { id: 2, label: t("carDetail.pastReservations") },
    ];

    return (
        <CarDetailSection
            title={t("carDetail.reservationHistory")}
            loading={loading}
            embedded
            className="!mt-0">
            <CarDetailSubTabs tabs={subTabs} active={active} onChange={setActive}>
                {currentList.length === 0 ? (
                    <div className="py-10">
                        <Empty description={emptyMessage} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-xl border border-slate-100">
                        <div className="max-h-96 overflow-y-auto">
                            <table className="sc-table min-w-[640px]">
                                <thead className="sticky top-0 z-[1] bg-slate-50/95 backdrop-blur-sm">
                                    <tr className="border-b border-slate-100 text-xs font-medium uppercase tracking-wide text-slate-500">
                                        <th className="px-4 py-3">{t("carDetail.resId")}</th>
                                        <th className="px-4 py-3">{t("carDetail.customer")}</th>
                                        <th className="px-4 py-3">{t("carDetail.pickupDate")}</th>
                                        <th className="px-4 py-3">{t("carDetail.dropoffDate")}</th>
                                        <th className="px-4 py-3 text-right">{t("view")}</th>
                                    </tr>
                                </thead>
                                <tbody className="fontRegular bodytt">{currentList.map(renderRow)}</tbody>
                            </table>
                        </div>
                    </div>
                )}
            </CarDetailSubTabs>
        </CarDetailSection>
    );
}

ResCustomer.propTypes = {
    reservations: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            resId: PropTypes.string,
            serialID: PropTypes.number,
            customerName: PropTypes.string,
            pickupDate: PropTypes.string,
            dropoffDate: PropTypes.string,
        }),
    ),
    pastReservations: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            resId: PropTypes.string,
            serialID: PropTypes.number,
            customerName: PropTypes.string,
            pickupDate: PropTypes.string,
            dropoffDate: PropTypes.string,
        }),
    ),
    id: PropTypes.string,
    loading: PropTypes.bool,
};

export default ResCustomer;
